import { useEffect, useState, useMemo } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from 'app/state'
import { RequestStatus } from 'features/common'
import { fetchLatestMapsRequest, fetchDemoMapsRequest } from 'features/maps/state/mapsSlice'
import BasicPage from 'features/common/ui/BasicPage/BasicPage'
import BasicPageContainer from 'features/common/ui/BasicPage/BasicPageContainer'
import HomeMaps from './HomeMaps/HomeMaps'
import HomeDynamicMaps from './HomeMaps/HomeDynamicMaps'
import HomeDatasets from './HomeDatasets/HomeDatasets'
import HomeApps from './HomeApps/HomeApps'
import HomeBlogLatestPosts from './HomeBlog/HomeBlogLatestPosts'
import Loader from 'features/common/ui/utils/Loader'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { fetchRecentResourcesRequest } from 'features/connections/state/connectionsSlice'
import { bookmarkedAppsRequest } from 'features/apps/state/appsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import HomeOnboarding from './HomeOnboarding/HomeOnboarding'
import useOnboardingChecklist, { OnboardingAction } from 'features/onboarding-checklist/utils/useOnboardingChecklist'
import { useAccountQuotas } from 'features/quotas/ui/hooks'
import { useHardQuotaModalController } from 'features/quotas/ui/WouldExceedQuotaDialog'
import MapsHardQuotaModal from 'features/maps/ui/MapsHardQuotaModal'
import { fetchBlogPostsRequest } from 'features/home/state/homeSlice'
import { QuotaUxTrigger } from 'features/hubspot/types'
import HomeModalNews from './HomeModalNews/HomeModalNews'
import { useNPS } from '../hooks/useNPS'
import HomeWorkflows from './HomeWorkflows/HomeWorkflows'
import { FormattedMessage } from 'react-intl'
import HomeDynamicWorkflows from './HomeWorkflows/HomeDynamicWorkflows'
import { useLatestWorkflows } from 'features/workflows/hooks/query'
import { useMeUser } from 'features/users/hooks/useMeUser'
import { getRoleInfo } from 'features/users/model/user'
import { UserRole } from 'features/users'
import MapsPage from 'features/maps/ui/MapsPage'
import { useAIAgentTasksQuery } from 'features/genAi-onboarding/hooks/genAiOnboardingQueries'
import OnboardingInitialDialog from 'features/onboarding-checklist/ui/OnboardingInitialDialog'
import { Video } from 'features/onboarding-checklist/types'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'

const MAX_DATASETS = 7

export function HomePage() {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isAgentAIV1Enabled = useFeatureFlag(FeatureFlag.GenAIV1)
  const maps = useAppSelector((state) => state.maps.latestMaps)
  const latestMapsStatus = useAppSelector((state) => state.maps.latestMapsStatus)
  const onboardingVideos = useAppSelector((state) => state.onboardingChecklist.videos)
  const { tasks } = useAIAgentTasksQuery()
  const [showAgenticGISDialog, setShowAgenticGISDialog] = useState(false)

  const isMdScreen = useMediaQuery(theme.breakpoints.only('md'))
  const isLargeOrUpScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const [mapsLoading, setMapsLoading] = useState(latestMapsStatus !== RequestStatus.Fulfilled)
  const onboardingChecklist = useAppSelector((state) => state.onboardingChecklist)
  const onboardingChecklistLoading =
    onboardingChecklist.status === RequestStatus.Idle || onboardingChecklist.status === RequestStatus.Pending
  const { isUserOnboarding } = useOnboardingChecklist()
  const { data: workflows } = useLatestWorkflows()
  const user = useMeUser()
  const isGuest = getRoleInfo(user).role === UserRole.Guest

  useNPS()

  const recentDatasetsLoading = useAppSelector(
    (state) =>
      state.connections.recentResourcesStatus === RequestStatus.Idle ||
      state.connections.recentResourcesStatus === RequestStatus.Pending
  )
  const bookmarkedAppsLoading = useAppSelector(
    (state) =>
      state.apps.bookmarkedAppsStatus === RequestStatus.Idle ||
      state.apps.bookmarkedAppsStatus === RequestStatus.Pending
  )
  const blogPostsLoading = useAppSelector(
    (state) => state.home.blogPostsStatus === RequestStatus.Idle || state.home.blogPostsStatus === RequestStatus.Pending
  )

  const demoMapsStatus = useAppSelector((state) => state.maps.demoMapsStatus)
  const demoMapsLoading = demoMapsStatus === RequestStatus.Idle || demoMapsStatus === RequestStatus.Pending
  const { mapsQuota } = useAccountQuotas()
  const hardQuotaModalController = useHardQuotaModalController(mapsQuota)

  useEffect(() => {
    const agenticGISVideo = tasks?.find((task) => task.id === OnboardingAction.WatchAgenticGISVideo)
    const workspaceVideo = onboardingVideos?.find((video) => video.id === OnboardingAction.WatchWorkspaceVideo)
    const hasAlreadyWatchedWorkspaceVideo = workspaceVideo?.completed || !isUserOnboarding
    const shouldShowAgenticGISDialog =
      hasAlreadyWatchedWorkspaceVideo && isAgentAIV1Enabled && agenticGISVideo?.completed === false
    if (shouldShowAgenticGISDialog) {
      setShowAgenticGISDialog(true)
    }
  }, [onboardingVideos, isUserOnboarding, isAgentAIV1Enabled, tasks])

  useEffect(() => {
    if (latestMapsStatus === RequestStatus.Idle) {
      dispatch(fetchLatestMapsRequest())
    }
  }, [dispatch, latestMapsStatus])

  useEffect(() => {
    dispatch(createMetricRequest(MetricEventType.OpenHomePage))
    if (isGuest) {
      return
    }

    // TODO: why is not checking the status?
    dispatch(fetchRecentResourcesRequest(MAX_DATASETS))
    dispatch(bookmarkedAppsRequest())
    dispatch(fetchDemoMapsRequest())
    dispatch(fetchBlogPostsRequest())
  }, [isGuest, dispatch])

  useEffect(() => {
    if (latestMapsStatus === RequestStatus.Fulfilled) {
      setMapsLoading(false)
    }
  }, [maps, isLargeOrUpScreen, isMdScreen, latestMapsStatus])

  const latestMaps = useMemo(() => maps.slice(0, isMdScreen ? 3 : 4), [maps, isMdScreen])
  const latestWorkflows = useMemo(() => workflows?.data?.slice(0, isMdScreen ? 3 : 4), [workflows?.data, isMdScreen])
  const hasWorkflows = latestWorkflows && latestWorkflows?.length > 0
  const hasMaps = latestMaps.length > 0

  if (isGuest) {
    return <MapsPage />
  }

  if (
    mapsLoading ||
    onboardingChecklistLoading ||
    recentDatasetsLoading ||
    bookmarkedAppsLoading ||
    demoMapsLoading ||
    blogPostsLoading
  ) {
    return (
      <Box mt={5}>
        <Loader />
      </Box>
    )
  }

  if (isUserOnboarding) {
    return (
      <>
        <HomeOnboarding
          hasMaps={hasMaps}
          latestMaps={latestMaps}
          hasWorkflows={hasWorkflows}
          latestWorkflows={latestWorkflows}
        />
        <OnboardingInitialDialog
          type={Video.AgenticGISBuilder}
          open={showAgenticGISDialog}
          onWatchVideo={() => setShowAgenticGISDialog(false)}
          onSkip={() => setShowAgenticGISDialog(false)}
        />
      </>
    )
  }

  return (
    <BasicPage contentClassName="fullWidth">
      <BasicPageContainer>
        <HomeApps />
        <HomeMaps
          title={hasMaps ? null : <FormattedMessage id="home.onboarding.maps.title" />}
          mapsLength={latestMaps.length}
        >
          {hasMaps ? <HomeDynamicMaps maps={latestMaps} /> : null}
        </HomeMaps>
        <HomeWorkflows
          workflowsLength={latestWorkflows?.length ?? 0}
          title={
            <FormattedMessage
              id={hasWorkflows ? 'home.actions.lastWorkflows.title' : 'home.onboarding.workflows.title'}
            />
          }
        >
          {hasWorkflows ? <HomeDynamicWorkflows workflows={latestWorkflows} /> : null}
        </HomeWorkflows>
        <HomeDatasets />
        <HomeBlogLatestPosts />
      </BasicPageContainer>
      <MapsHardQuotaModal {...hardQuotaModalController.props} variant="create" quotaUxTrigger={QuotaUxTrigger.NewMap} />
      <HomeModalNews />
      <OnboardingInitialDialog
        type={Video.AgenticGISBuilder}
        open={showAgenticGISDialog}
        onWatchVideo={() => setShowAgenticGISDialog(false)}
        onSkip={() => setShowAgenticGISDialog(false)}
      />
    </BasicPage>
  )
}

export default HomePage
